import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { axiosFetcher } from '@odin/huginn-client/axios';
import { FeedbackGate } from '@odin/huginn-client/ui';
import React, { Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import AuthCallback from '../components/AuthCallback';
import LocalLoginForm from '../components/LocalLoginForm';
import Login from '../components/LoginForm';
import { usePermissions } from '../permissions';
import { isPathAllowedForExternalRole, useActiveExternalRole } from '../permissions/external-roles';
import { appBackground } from '../services';
import { useInterval } from '../services/helpers';
import NavBar from './components/NavBar';
import SideBar from './components/SideBar';
import { NAVBAR_HEIGHT, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_OPEN } from './constants/layout';
import { AppHistoryProvider } from './navigation/AppHistoryContext';
import { HomePanel, getPanelUri, hiddenPanels, panels } from './panels';
import { CustomErrorBoundary } from './utils/Errors/ErrorBoundary';

const REFRESH_PERMS_INTERVAL = 1000 * 60 * 10; // 10 minutes

const PAGE_TRANSITION_MS = 180;

/**
 * Inner component — must live inside <Router> to call useLocation.
 * Keying the Box on the pathname causes React to unmount/remount it on
 * navigation, so there is only ever ONE page in the DOM at a time.
 * The enter animation is driven by a CSS keyframe declared inline via sx.
 */
function AnimatedRoutes({ theme }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const externalRole = useActiveExternalRole();

    // External-only users are confined to the slice of the app their role
    // declares (see EXTERNAL_ROLES in permissions/external-roles.js). Once
    // their profile resolves we redirect them onto the role's landing path,
    // and any subsequent attempt to navigate outside the allowed prefixes
    // bounces back. The guard bails out when the pathname already lives
    // under an allowed prefix, so there is no loop.
    React.useEffect(() => {
        if (!externalRole) return;
        const allowed = isPathAllowedForExternalRole(externalRole, location.pathname);
        if (!allowed) {
            navigate(externalRole.landingPath, { replace: true });
        }
    }, [externalRole, location.pathname, navigate]);

    const allowedPanels = panels.filter((p) => !p.requiredPermission || hasPermission(p.requiredPermission));
    const allowedHiddenPanels = hiddenPanels.filter(
        (p) => !p.requiredPermission || hasPermission(p.requiredPermission),
    );

    return (
        <Box
            key={location.pathname}
            sx={{
                flex: 1,
                overflowY: 'scroll',
                overflowX: 'hidden',
                padding: theme.spacing(2.5),
                paddingRight: theme.spacing(3.5),
                '@keyframes pageFadeIn': {
                    from: { opacity: 0, transform: 'translateY(6px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `pageFadeIn ${PAGE_TRANSITION_MS}ms ease-out`,
            }}
        >
            <CustomErrorBoundary name={'Main content'}>
                <Suspense
                    fallback={
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                            <CircularProgress />
                        </Box>
                    }
                >
                    <Routes location={location}>
                        {allowedPanels.map((panel, index) => (
                            <Route key={index} path={panel.uri} element={panel.component} />
                        ))}
                        {allowedHiddenPanels.map((panel, index) => (
                            <Route key={`hidden-${index}`} path={panel.uri} element={panel.component} />
                        ))}
                        <Route path='*' element={<Navigate to={getPanelUri(<HomePanel />)} replace />} />
                    </Routes>
                </Suspense>
            </CustomErrorBoundary>
        </Box>
    );
}

function DashboardDrawer() {
    const theme = useTheme();
    const { user, loggedIn, checking, refresh } = useAuth();

    const [open, setOpen] = React.useState(() => localStorage.getItem('sidebarOpen') === 'true');

    const handleToggle = () => {
        setOpen((prev) => {
            const next = !prev;
            localStorage.setItem('sidebarOpen', String(next));
            return next;
        });
    };

    // Proactive token refresh: fire when the token is within 60s of expiry, or
    // every REFRESH_PERMS_INTERVAL (10 min) as a fallback for permission syncing.
    // This prevents mid-session 401s when grace tokens (5 min TTL) expire before
    // the fixed interval fires.
    useInterval(() => {
        if (!loggedIn) return;
        const expiresAt = user?.expiresAt;
        if (expiresAt && Date.now() >= expiresAt - 60000) {
            void refresh();
        }
    }, 30000); // check every 30s
    useInterval(() => {
        if (loggedIn) void refresh();
    }, REFRESH_PERMS_INTERVAL);

    return (
        <Box sx={{ display: 'flex', height: '100vh', overflowX: 'hidden' }}>
            <CssBaseline />
            <Router>
                <AppHistoryProvider>
                    <FeedbackGate
                        fetcher={axiosFetcher}
                        theme={theme.palette.mode}
                        adhocTrigger='none'
                        tokens={{ font: 'inherit' }}
                        // The gate wraps the login and callback routes too, and its
                        // prompt fetch is authenticated: a 401 here would be turned
                        // into a token refresh by the axios interceptor, racing the
                        // callback's own token exchange.
                        enabled={loggedIn && !checking}
                    >
                        <Routes>
                            <Route path='/auth/callback' element={<AuthCallback />} />
                            <Route path='/auth/local-login' element={<LocalLoginForm />} />
                            <Route
                                path='*'
                                element={
                                    !loggedIn && !checking ? (
                                        <Login />
                                    ) : (
                                        <>
                                            <NavBar isOpen={open} onToggle={handleToggle} />
                                            <SideBar isOpen={open} onToggle={handleToggle} />
                                            <Box
                                                sx={{
                                                    marginLeft: open
                                                        ? `${SIDEBAR_WIDTH_OPEN}px`
                                                        : `${SIDEBAR_WIDTH_COLLAPSED}px`,
                                                    transition: theme.transitions.create('margin-left', {
                                                        easing: theme.transitions.easing.easeInOut,
                                                        duration: 240,
                                                    }),
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flex: 1,
                                                    paddingBottom: 0,
                                                    background: appBackground(theme.palette.mode),
                                                    backgroundAttachment: 'fixed',
                                                }}
                                            >
                                                {!checking && (
                                                    <main
                                                        style={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                flexShrink: 0,
                                                                display: 'flex',
                                                                justifyContent: 'flex-end',
                                                                padding: theme.spacing(0, 1),
                                                                minHeight: NAVBAR_HEIGHT,
                                                            }}
                                                        />
                                                        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                                                            <AnimatedRoutes theme={theme} />
                                                        </Box>
                                                    </main>
                                                )}
                                            </Box>
                                        </>
                                    )
                                }
                            />
                        </Routes>
                    </FeedbackGate>
                </AppHistoryProvider>
            </Router>
        </Box>
    );
}

export default DashboardDrawer;
